import { useState, useEffect } from 'react';

const SOL_DEC = 1_000_000_000;
function getApiKey() {
  return localStorage.getItem('helius-api-key') || process.env.REACT_APP_HELIUS_API_KEY || '';
}
const HELIUS  = 'https://api.helius.xyz/v0';
const TOKEN_PGM = 'TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA';
const BOT_THRESHOLD_TPD = 30;

function getRpcEndpoints() {
  const key = getApiKey();
  return [
    'https://api.mainnet-beta.solana.com',
    'https://solana-mainnet.g.alchemy.com/v2/demo',
    'https://rpc.ankr.com/solana',
    ...(key ? [`https://mainnet.helius-rpc.com/?api-key=${key}`] : []),
  ];
}

const tokenCache = {};

async function rpc(method, params = []) {
  let lastErr = null;
  for (const url of getRpcEndpoints()) {
    try {
      const r = await fetch(url, { method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify({jsonrpc:'2.0',id:1,method,params}), signal: AbortSignal.timeout(6000) });
      if (!r.ok) {
        if (r.status === 429 || r.status === 403) {
          await new Promise(res => setTimeout(res, 500));
          throw new Error(`HTTP ${r.status}`);
        }
        throw new Error(`HTTP ${r.status}`);
      }
      const j = await r.json();
      if (j.error) throw new Error(j.error?.message || JSON.stringify(j.error));
      return j.result;
    } catch (e) { lastErr = e; }
  }
  throw new Error(`${method}: ${lastErr?.message || 'échec'}`);
}

//  Désactivé pour éviter les erreurs 429
async function getSolBalance(addr) {
  return 0;
}

async function getTokenAccounts(addr) {
  return [];
}

async function getTokenMeta(mint) {
  if (tokenCache[mint]) return tokenCache[mint];
  try {
    const r = await fetch(`https://api.dexscreener.com/latest/dex/tokens/${mint}`, { signal: AbortSignal.timeout(5000) });
    const j = await r.json();
    const pair = j?.pairs?.[0];
    if (pair?.baseToken) {
      const meta = { name: pair.baseToken.name || mint.slice(0,10), symbol: pair.baseToken.symbol || mint.slice(0,6), logoURI: pair.info?.imageUrl || null, pairCreatedAt: pair.pairCreatedAt ? Math.round(pair.pairCreatedAt / 1000) : null };
      tokenCache[mint] = meta; return meta;
    }
  } catch {}
  try {
    const r = await fetch(`https://tokens.jup.ag/token/${mint}`, { signal: AbortSignal.timeout(5000) });
    if (r.ok) {
      const j = await r.json();
      const meta = { name: j.name || mint.slice(0,10), symbol: j.symbol || mint.slice(0,6), logoURI: j.logoURI || null };
      tokenCache[mint] = meta; return meta;
    }
  } catch {}
  const meta = { name: mint.slice(0,10)+'…', symbol: mint.slice(0,6).toUpperCase(), logoURI: null };
  tokenCache[mint] = meta; return meta;
}

async function getEnhancedTxns(addr, limit = 100) {
  if (!getApiKey()) return [];
  const r = await fetch(`${HELIUS}/addresses/${addr}/transactions?api-key=${getApiKey()}&limit=${limit}`, { signal: AbortSignal.timeout(8000) });
  if (!r.ok) {
    if (r.status === 429) throw new Error('Limite de requêtes API atteinte (429). Attendez quelques minutes et réessayez.');
    if (r.status === 401 || r.status === 403) throw new Error(`Clé API Helius invalide (${r.status}). Vérifie ta clé sur helius.dev`);
    throw new Error(`Erreur API Helius (${r.status})`);
  }
  return r.json();
}

async function getEnhancedTxnsDeep(addr, target = 500, pageSize = 100, maxPages = 5) {
  if (!getApiKey()) throw new Error('Clé API Helius manquante.');
  let all = [], before = null;
  for (let page = 0; page < maxPages; page++) {
    let url = `${HELIUS}/addresses/${addr}/transactions?api-key=${getApiKey()}&limit=${pageSize}`;
    if (before) url += `&before=${before}`;
    const r = await fetch(url, { signal: AbortSignal.timeout(8000) });
    if (!r.ok) {
      if (r.status === 429) throw new Error('Limite de requêtes API atteinte (429). Attendez quelques minutes et réessayez.');
      if (r.status === 401 || r.status === 403) throw new Error(`Clé API Helius invalide (${r.status}). Vérifie ta clé sur helius.dev`);
      throw new Error(`Erreur API Helius (${r.status})`);
    }
    const chunk = await r.json();
    if (!Array.isArray(chunk) || chunk.length === 0) break;
    all = all.concat(chunk);
    if (all.length >= target) break;
    const last = chunk[chunk.length - 1];
    if (!last?.signature) break;
    before = last.signature;
    await new Promise(res => setTimeout(res, 200));
  }
  return all;
}

async function getSolPrice() {
  try {
    const r = await fetch('https://api.binance.com/api/v3/ticker/price?symbol=SOLUSDT', { signal: AbortSignal.timeout(5000) });
    const j = await r.json();
    return parseFloat(j.price) || 150;
  } catch { return 150; }
}

function capRoi(roi) { return Math.max(-9999, Math.min(9999, roi)); }
function fmtRoi(roi) { const c = capRoi(roi); return `${c >= 0 ? '+': ''}${c.toFixed(1)}%`; }

function fmtUsd(n, forceSign = false) {
  const sign = n >= 0 ? (forceSign ? '+': '') : '-';
  const abs = Math.abs(n);
  if (abs > 0 && abs < 1) return `${sign}$${abs.toFixed(2)}`;
  if (abs >= 1e6) return `${sign}$${(abs/1e6).toFixed(2)}M`;
  if (abs >= 1e3) return `${sign}$${(abs/1e3).toFixed(1)}K`;
  return `${sign}$${abs.toFixed(0)}`;
}

function fmtInvestedUsd(n) {
  const v = Math.abs(n);
  if (v >= 1e6) return `$${(v/1e6).toFixed(2)}M`;
  if (v >= 1e3) return `$${(v/1e3).toFixed(1)}K`;
  if (v >= 1)   return `$${v.toFixed(0)}`;
  if (v > 0)    return `$${v.toFixed(2)}`;
  return '$0';
}

function fmtDuree(min) {
  if (min === null || min === undefined) return '—';
  if (min === 0) return '< 1min';
  if (min < 60) return `${min}min`;
  if (min < 1440) return `${Math.round(min/60)}h`;
  return `${Math.round(min/1440)}j`;
}

function fmtDate(ts) {
  return new Date(ts * 1000).toLocaleDateString('fr-FR', { day:'2-digit', month:'2-digit', year:'numeric'});
}

function buildHeatmap(txns, addr, solPrice) {
  const heuresPnl = Array(24).fill(0).map(() => ({ pnl: 0, count: 0 }));
  const joursPnl  = Array(7).fill(0).map(() => ({ pnl: 0, count: 0 }));
  for (const tx of txns) {
    if (tx.type !== 'SWAP'|| !tx.timestamp) continue;
    const d = new Date(tx.timestamp * 1000);
    const heure = d.getHours();
    const jour  = (d.getDay() + 6) % 7;
    let solIn = 0, solOut = 0;
    for (const nt of tx.nativeTransfers || []) {
      if (nt.toUserAccount === addr) solIn += nt.amount / SOL_DEC;
      if (nt.fromUserAccount === addr) solOut += nt.amount / SOL_DEC;
    }
    const delta = (solIn - solOut) * solPrice;
    heuresPnl[heure].pnl += delta; heuresPnl[heure].count += 1;
    joursPnl[jour].pnl += delta; joursPnl[jour].count += 1;
  }
  let bestHeure = 0, bestHeurePnl = -Infinity;
  heuresPnl.forEach((h, i) => { if (h.count > 0 && h.pnl > bestHeurePnl) { bestHeurePnl = h.pnl; bestHeure = i; } });
  const joursNoms = ['Lun','Mar','Mer','Jeu','Ven','Sam','Dim'];
  let bestJour = 0, bestJourPnl = -Infinity;
  joursPnl.forEach((j, i) => { if (j.count > 0 && j.pnl > bestJourPnl) { bestJourPnl = j.pnl; bestJour = i; } });
  return {
    heuresPnl, joursPnl,
    bestHeure: heuresPnl[bestHeure].count > 0 ? `${bestHeure}h-${bestHeure+1}h`: '—',
    bestJour:  joursPnl[bestJour].count  > 0 ? joursNoms[bestJour] : '—',
  };
}

function buildPnlChart(txns, addr, solPrice) {
  const book = {};
  const points = [];
  let cumul = 0;
  const swaps = txns.filter(tx => tx.type === 'SWAP' && tx.timestamp).sort((a,b) => a.timestamp - b.timestamp);
  for (const tx of swaps) {
    let solIn = 0, solOut = 0;
    for (const nt of tx.nativeTransfers || []) {
      if (nt.toUserAccount === addr) solIn += nt.amount / SOL_DEC;
      if (nt.fromUserAccount === addr) solOut += nt.amount / SOL_DEC;
    }
    const walletTT = (tx.tokenTransfers || []).filter(tt => {
      const amt = parseFloat(tt.tokenAmount ?? 0);
      return amt > 0 && (tt.toUserAccount === addr || tt.fromUserAccount === addr);
    });
    const mints = [...new Set(walletTT.map(tt => tt.mint))];
    const solPerMint = mints.length > 0 ? Math.abs(solIn - solOut) / mints.length : 0;
    let hadSell = false;
    for (const tt of walletTT) {
      const amt = parseFloat(tt.tokenAmount ?? 0);
      if (!amt) continue;
      const mint = tt.mint;
      const isBuy = tt.toUserAccount === addr;
      if (!book[mint]) book[mint] = { invested: 0, tokens: 0 };
      if (isBuy) {
        book[mint].invested += solPerMint;
        book[mint].tokens += amt;
      } else {
        const pos = book[mint];
        const pct = pos.tokens > 0 ? Math.min(amt / pos.tokens, 1) : 0;
        const costBasis = pos.invested * pct;
        cumul += (solPerMint - costBasis) * solPrice;
        pos.invested = Math.max(0, pos.invested - costBasis);
        pos.tokens = Math.max(0, pos.tokens - amt);
        hadSell = true;
      }
    }
    if (hadSell) {
      points.push({ ts: tx.timestamp, pnl: cumul, date: new Date(tx.timestamp * 1000).toLocaleDateString('fr-FR', { day:'2-digit', month:'2-digit' }) });
    }
  }
  if (points.length > 100) {
    const step = Math.ceil(points.length / 100);
    return points.filter((_, i) => i % step === 0 || i === points.length - 1);
  }
  return points;
}

export function calcPnlCalendar(closedPositions, month, year) {
  const map = {};
  for (const pos of closedPositions) {
    if (!pos.sortieTs || pos.resultat === 'En cours') continue;
    const d = new Date(pos.sortieTs * 1000);
    if (d.getMonth() !== month || d.getFullYear() !== year) continue;
    const day = d.getDate();
    map[day] = (map[day] || 0) + pos.pnlUsd;
  }
  return map;
}

async function calcPnL(txns, addr, solPrice) {
  const book = {};
  const tradesList = [];

  for (const tx of txns) {
    if (tx.type !== 'SWAP') continue;
    let solIn = 0, solOut = 0;
    for (const nt of tx.nativeTransfers || []) {
      if (nt.toUserAccount === addr) solIn += nt.amount / SOL_DEC;
      if (nt.fromUserAccount === addr) solOut += nt.amount / SOL_DEC;
    }
    const solDelta = solIn - solOut;
    const walletTokenTransfers = (tx.tokenTransfers || []).filter(tt => {
      const amt = parseFloat(tt.tokenAmount ?? 0);
      if (!amt) return false;
      return tt.toUserAccount === addr || tt.fromUserAccount === addr;
    });
    const mintsInTx = [...new Set(walletTokenTransfers.map(tt => tt.mint))];
    const solPerMint = mintsInTx.length > 0 ? Math.abs(solDelta) / mintsInTx.length : 0;

    for (const tt of walletTokenTransfers) {
      const amt = parseFloat(tt.tokenAmount ?? 0);
      if (!amt) continue;
      const mint = tt.mint;
      if (!book[mint]) {
        book[mint] = {
          sym: tt.tokenSymbol || mint.slice(0,6).toUpperCase(),
          name: tt.tokenName  || mint.slice(0,10)+'…',
          image: null, mint, buys: [], sells: [], firstBuyTs: null, pairCreatedAt: null,
        };
      }
      const isBuy = tt.toUserAccount === addr;
      const costSol = solPerMint;
      const costUsd = costSol * solPrice;
      if (isBuy) {
        book[mint].buys.push({ amt, costSol, ts: tx.timestamp });
        if (!book[mint].firstBuyTs) book[mint].firstBuyTs = tx.timestamp;
      } else {
        book[mint].sells.push({ amt, revSol: costSol, ts: tx.timestamp });
      }
      tradesList.push({
        signature: tx.signature, timestamp: tx.timestamp, mint,
        symbol: tt.tokenSymbol || mint.slice(0,6).toUpperCase(),
        name: tt.tokenName || mint.slice(0,10)+'…',
        image: null, type: isBuy ? 'BUY': 'SELL',
        amtSol: costSol, amtUsd: costUsd, tokens: amt,
      });
    }
  }

  const mints = Object.keys(book)
    .sort((a,b) => ((book[b].buys?.length||0)+(book[b].sells?.length||0)) - ((book[a].buys?.length||0)+(book[a].sells?.length||0)))
    .slice(0, 30);
  const metas = await Promise.all(mints.map(m => getTokenMeta(m)));
  mints.forEach((mint, i) => {
    const meta = metas[i];
    if (meta?.symbol && meta.symbol.length > 1) book[mint].sym = meta.symbol;
    if (meta?.name   && meta.name.length   > 3) book[mint].name = meta.name;
    if (meta?.logoURI) book[mint].image = meta.logoURI;
    if (meta?.pairCreatedAt) book[mint].pairCreatedAt = meta.pairCreatedAt;
  });

  tradesList.forEach(t => {
    if (book[t.mint]) { t.symbol = book[t.mint].sym; t.name = book[t.mint].name; t.image = book[t.mint].image; }
  });

  let totPnlSol = 0, totCostSol = 0, wins = 0, losses = 0, gainSol = 0, lossSol = 0;
  const rows = [], closedPositions = [];
  let bestTrade = null, worstTrade = null;
  let maxStreak = 0;
  let totalDureeMin = 0, dureeCount = 0;

  for (const pos of Object.values(book)) {
    if (!pos.buys.length) continue;
    const costSol = pos.buys.reduce((s,b) => s+b.costSol, 0);
    const revSol  = pos.sells.reduce((s,s2) => s+s2.revSol, 0);
    const trades  = pos.buys.length + pos.sells.length;
    const pnlSol  = revSol - costSol;
    const costUsd = costSol * solPrice;
    const revUsd  = revSol * solPrice;
    const pnlUsd  = pnlSol * solPrice;
    const roi     = costSol > 0 ? (pnlSol / costSol) * 100 : 0;
    const hasEnoughData = trades >= 2 && costUsd >= 1;
    const firstBuy = pos.buys.reduce((m,b) => b.ts < m ? b.ts : m, pos.buys[0].ts);
    const lastSell = pos.sells.length > 0 ? pos.sells.reduce((m,s) => s.ts > m ? s.ts : m, pos.sells[0].ts) : null;
    const dureeMin = lastSell ? Math.round((lastSell - firstBuy) / 60) : null;
    if (dureeMin !== null && dureeMin >= 0) { totalDureeMin += dureeMin; dureeCount++; }
    const isClosed = pos.sells.length > 0;
    if (isClosed) {
      totPnlSol += pnlSol; totCostSol += costSol;
      if (hasEnoughData) {
        if (pnlSol >= 0) { wins++; gainSol += pnlSol; }
        else { losses++; lossSol += Math.abs(pnlSol); }
      }
    }
    const roiCapped = capRoi(roi);
    if (isClosed && hasEnoughData) {
      if (!bestTrade  || pnlUsd > bestTrade.pnlUsd)  bestTrade  = { symbol: pos.sym, name: pos.name, image: pos.image, pnlUsd, roi: roiCapped, roiStr: fmtRoi(roi) };
      if (!worstTrade || pnlUsd < worstTrade.pnlUsd) worstTrade = { symbol: pos.sym, name: pos.name, image: pos.image, pnlUsd, roi: roiCapped, roiStr: fmtRoi(roi) };
    }
    rows.push({
      symbol: pos.sym, name: pos.name, image: pos.image, mint: pos.mint,
      firstBuyTs: pos.firstBuyTs,
      roi:     hasEnoughData ? fmtRoi(roi) : 'N/A',
      roiNum:  hasEnoughData ? roiCapped : 0,
      trades, investi: fmtInvestedUsd(costUsd), dureeMin,
      statut:  !isClosed ? 'En cours' : !hasEnoughData ? 'Données faibles': roiCapped >= 200 ? 'Très rentable': roiCapped >= 0 ? 'Rentable': 'Non rentable',
      level:   !hasEnoughData ? 1 : roiCapped >= 200 ? 3 : roiCapped >= 0 ? 2 : 1,
      isReliable: hasEnoughData, costUsd, revUsd, pnlUsd,
    });
    closedPositions.push({
      mint: pos.mint, symbol: pos.sym, name: pos.name, image: pos.image,
      entreeTs: firstBuy, sortieTs: lastSell,
      achatUsd: costUsd, venteUsd: revUsd, pnlUsd,
      roi:    hasEnoughData ? roiCapped : null,
      roiStr: hasEnoughData ? fmtRoi(roi) : 'N/A',
      roiPos: roiCapped >= 0, dureeMin,
      resultat: !isClosed ? 'En cours' : !hasEnoughData ? 'Données insuffisantes' : pnlSol >= 0 ? 'Gagnant' : 'Perdant',
      nbVentes: pos.sells.length,
    });
  }

  const closedChron = closedPositions
    .filter(p => p.resultat === 'Gagnant' || p.resultat === 'Perdant')
    .sort((a, b) => (a.sortieTs || 0) - (b.sortieTs || 0));
  let streak = 0;
  for (const cp of closedChron) {
    if (cp.resultat === 'Gagnant') { streak++; if (streak > maxStreak) maxStreak = streak; }
    else streak = 0;
  }

  closedPositions.sort((a,b) => b.entreeTs - a.entreeTs);
  rows.sort((a,b) => b.roiNum - a.roiNum);
  const total = wins + losses;
  const overallR = totCostSol > 0 ? capRoi((totPnlSol / totCostSol) * 100) : 0;
  const avgGainUsd = wins   > 0 ? (gainSol * solPrice / wins)   : 0;
  const avgLossUsd = losses > 0 ? (lossSol * solPrice / losses) : 0;
  const rrRatio    = avgLossUsd > 0 ? (avgGainUsd / avgLossUsd) : null;
  const dureeMoyMin = dureeCount > 0 ? Math.round(totalDureeMin / dureeCount) : null;

  const entryDelays = [];
  for (const pos of Object.values(book)) {
    if (pos.firstBuyTs && pos.pairCreatedAt && pos.firstBuyTs >= pos.pairCreatedAt) {
      const delayMin = Math.round((pos.firstBuyTs - pos.pairCreatedAt) / 60);
      if (delayMin >= 0 && delayMin < 43200) entryDelays.push(delayMin);
    }
  }
  const avgEntryDelayMin = entryDelays.length >= 3 ? Math.round(entryDelays.reduce((s,v) => s+v, 0) / entryDelays.length) : null;

  return {
    pnlSol: totPnlSol, pnlUsd: totPnlSol * solPrice,
    overallRoi: overallR, winRate: total > 0 ? (wins / total) * 100 : 0,
    totalTrades: total, wins, losses,
    gainMoyenUsd: avgGainUsd, perteMoyUsd: avgLossUsd,
    rrRatio, maxStreak, bestTrade, worstTrade, dureeMoyMin,
    tokenRows: rows.slice(0, 50),
    tradesList: tradesList.sort((a,b) => b.timestamp - a.timestamp),
    closedPositions,
    totalAchatUsd: rows.reduce((s,r) => s + (r.costUsd||0), 0),
    totalVenteUsd: rows.reduce((s,r) => s + (r.revUsd||0), 0),
    avgEntryDelayMin,
  };
}

function buildFollowVerdict({ overallRoi, winRate, totalTrades, tradesPerDay, daysActive, rrRatio, avgGainUsd, avgLossUsd, bestTradePnl, totalPnlUsd, closedPositions, avgEntryDelayMin }) {
  if (totalTrades === 0) {
    return { score: 0, recommendation: 'Ne pas suivre', risk: 'Élevé', positives: [],
      warnings: ['Aucune position clôturée — impossible d\'évaluer la rentabilité'] };
  }
  const positives = [], warnings = [];
  let score = 25; // doit être mérité, pas offert

  // 1. Win rate — critère principal
  if (winRate >= 70) { positives.push(`Win rate excellent (${winRate.toFixed(0)}%)`); score += 28; }
  else if (winRate >= 55) { positives.push(`Win rate élevé (${winRate.toFixed(0)}%)`); score += 16; }
  else if (winRate >= 45) { score += 6; }
  else if (winRate > 0 && winRate < 40) { warnings.push(`Win rate faible (${winRate.toFixed(0)}%)`); score -= 18; }
  else if (winRate === 0 && totalTrades > 0) { warnings.push('Aucune victoire détectée'); score -= 25; }

  // 2. ROI global
  if (overallRoi >= 100) { positives.push(`ROI exceptionnel (+${overallRoi.toFixed(0)}%)`); score += 20; }
  else if (overallRoi >= 30) { positives.push(`ROI solide (+${overallRoi.toFixed(0)}%)`); score += 12; }
  else if (overallRoi >= 10) { positives.push(`ROI positif (+${overallRoi.toFixed(0)}%)`); score += 6; }
  else if (overallRoi < 0) { warnings.push(`ROI négatif (${overallRoi.toFixed(0)}%)`); score -= 18; }

  // 3. Ratio Gain/Perte (R/R) — si on gagne plus qu'on perd en moyenne
  if (rrRatio !== null && !isNaN(rrRatio) && rrRatio > 0) {
    if (rrRatio >= 3) { positives.push(`Ratio G/P exceptionnel (${rrRatio.toFixed(1)}x)`); score += 18; }
    else if (rrRatio >= 1.5) { positives.push(`Bon ratio gain/perte (${rrRatio.toFixed(1)}x)`); score += 10; }
    else if (rrRatio >= 1) { score += 4; }
    else if (rrRatio < 0.7) { warnings.push(`Pertes supérieures aux gains (ratio ${rrRatio.toFixed(1)}x)`); score -= 12; }
  }

  // 4. Espérance de gain par trade (EV)
  if (avgGainUsd > 0 && avgLossUsd > 0 && winRate > 0) {
    const ev = (winRate / 100) * avgGainUsd - ((100 - winRate) / 100) * avgLossUsd;
    if (ev > 50) { positives.push(`Espérance positive (+$${ev.toFixed(0)}/trade en moyenne)`); score += 8; }
    else if (ev > 0) { positives.push(`Espérance légèrement positive`); score += 3; }
    else { warnings.push(`Espérance négative ($${ev.toFixed(0)}/trade)`); score -= 8; }
  }

  // 5. Volume de données
  if (totalTrades >= 30) { positives.push(`${totalTrades} trades — statistiques fiables`); score += 10; }
  else if (totalTrades >= 15) { positives.push(`${totalTrades} trades analysés`); score += 5; }
  else if (totalTrades > 0 && totalTrades < 8) { warnings.push(`Seulement ${totalTrades} trades — peu fiable`); score -= 15; }

  // 6. Ancienneté
  if (daysActive < 7) { warnings.push('Wallet trop récent (< 7 jours)'); score -= 12; }
  else if (daysActive >= 60) { positives.push(`Wallet actif depuis ${daysActive} jours`); score += 6; }
  else if (daysActive >= 14) { score += 3; }

  // 7. Détection bot
  if (tradesPerDay > BOT_THRESHOLD_TPD) { warnings.push('Activité suspecte — probable bot'); score -= 20; }
  else if (tradesPerDay > 15) { warnings.push(`Trading très fréquent (${tradesPerDay.toFixed(0)} trades/jour)`); score -= 8; }

  // 8. Concentration du ROI sur un seul trade
  if (bestTradePnl != null && totalPnlUsd > 0 && bestTradePnl > 0 && (bestTradePnl / totalPnlUsd) > 0.70) {
    warnings.push(`ROI concentré sur un seul trade (${((bestTradePnl / totalPnlUsd) * 100).toFixed(0)}% du total)`);
    score -= 15;
  }

  // 9. Régularité — PNL par périodes de 2 semaines
  if (closedPositions && closedPositions.length >= 3) {
    const cp = closedPositions
      .filter(p => p.sortieTs && typeof p.pnlUsd === 'number' && (p.resultat === 'Gagnant' || p.resultat === 'Perdant'))
      .sort((a, b) => a.sortieTs - b.sortieTs);
    if (cp.length >= 3) {
      const firstTs = cp[0].sortieTs;
      const buckets = {};
      for (const p of cp) {
        const b = Math.floor((p.sortieTs - firstTs) / (14 * 86400));
        buckets[b] = (buckets[b] || 0) + p.pnlUsd;
      }
      const vals = Object.values(buckets);
      const tot = vals.reduce((s, v) => s + v, 0);
      const maxB = Math.max(...vals);
      if (tot > 0 && vals.length > 1 && maxB / tot > 0.60) {
        warnings.push(`PNL concentré sur 2 semaines (${((maxB / tot) * 100).toFixed(0)}% du total)`);
        score -= 10;
      }
    }
  }

  // 10. Performance récente (30 derniers jours)
  if (closedPositions && closedPositions.length >= 3) {
    const cutoff = Date.now() / 1000 - 30 * 86400;
    const recent = closedPositions.filter(p => p.sortieTs && p.sortieTs >= cutoff && (p.resultat === 'Gagnant' || p.resultat === 'Perdant'));
    if (recent.length >= 3) {
      const recentWr = (recent.filter(p => p.resultat === 'Gagnant').length / recent.length) * 100;
      if (winRate - recentWr > 20) {
        warnings.push(`Dégradation récente : win rate 30j ${recentWr.toFixed(0)}% vs global ${winRate.toFixed(0)}%`);
        score -= 12;
      }
    }
  }

  // 11. Timing d'entrée (délai moyen après lancement du token)
  if (avgEntryDelayMin !== null) {
    if (avgEntryDelayMin < 10) { positives.push(`Sniper précoce — entrée avg ${avgEntryDelayMin < 1 ? '< 1' : avgEntryDelayMin}min après launch`); score += 10; }
    else if (avgEntryDelayMin > 1440) { warnings.push(`Entrée tardive en moyenne (${Math.round(avgEntryDelayMin / 60)}h après launch)`); score -= 5; }
  }

  score = Math.max(0, Math.min(100, Math.round(score)));
  let recommendation = score >= 70 ? 'Suivre' : score <= 39 ? 'Ne pas suivre' : 'Observer';
  if (totalTrades < 10 && recommendation === 'Suivre') {
    recommendation = 'Observer';
    warnings.push(`Moins de 10 trades (${totalTrades}) — verdict plafonné à Observer`);
  }
  const risk = recommendation === 'Suivre' ? 'Faible' : recommendation === 'Ne pas suivre' ? 'Élevé' : 'Moyen';
  return { score, recommendation, risk, positives, warnings };
}

function buildConfidence({ txCount, swapCount, daysActive, hasApiKey, closedTradesCount }) {
  if (!hasApiKey) return { score: 20, level: 'Faible', note: 'Données partielles: clé API absente.'};
  let score = 30;
  if (txCount >= 300) score += 35; else if (txCount >= 150) score += 25; else if (txCount >= 60) score += 15; else if (txCount >= 20) score += 8;
  if (swapCount >= 40) score += 20; else if (swapCount >= 15) score += 12; else if (swapCount >= 6) score += 6;
  if (daysActive >= 90) score += 15; else if (daysActive >= 30) score += 10; else if (daysActive >= 7) score += 5;
  if (closedTradesCount === 0 && swapCount >= 5) score -= 20;
  score = Math.max(0, Math.min(100, score));
  const level = score >= 75 ? 'Élevée': score <= 44 ? 'Faible': 'Moyenne';
  const note  = closedTradesCount === 0 && swapCount >= 5
    ? 'Aucune position clôturée — impossible d\'évaluer la rentabilité réelle.'
    : score >= 75 ? 'Bonne profondeur historique: décision plus fiable.'
    : score <= 44 ? 'Échantillon limité: décision à confirmer.'
    : 'Base suffisante pour une décision prudente.';
  return { score, level, note };
}export function useWalletAnalysis(address, refreshKey = 0) {
  const [state, setState] = useState(() => {
    const addr = (address || '').trim();
    if (!addr || addr.includes('...') || addr.length < 32) return { data: null, loading: false, error: null };
    try {
      const cached = localStorage.getItem(`wallet-analysis-${addr}`);
      if (cached) {
        const { data } = JSON.parse(cached);
        if (data) return { data, loading: false, error: null };
      }
    } catch {}
    return { data: null, loading: false, error: null };
  });

  useEffect(() => {
    const addr = (address || '').trim();
    if (!addr || addr.includes('...') || addr.length < 32) { setState({ data: null, loading: false, error: null }); return; }

    const cacheKey = `wallet-analysis-${addr}`;
    try {
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        const { data } = JSON.parse(cached);
        if (data) { setState(prev => prev.data ? prev : { data, loading: false, error: null }); return; }
      }
    } catch {}

    setState(prev => ({ ...prev, loading: true, error: null }));
    let cancelled = false;

    (async () => {
      try {
        const [solPrice, solBal, tokenAccs, txns] = await Promise.all([
          getSolPrice(), getSolBalance(addr), getTokenAccounts(addr),
          getEnhancedTxnsDeep(addr, 500, 100, 5),
        ]);
        if (cancelled) return;

        const enhancedTxns = txns.length > 0 ? txns : await getEnhancedTxns(addr, 100);
        const pnl      = await calcPnL(enhancedTxns, addr, solPrice);
        const heatmap  = buildHeatmap(enhancedTxns, addr, solPrice);
        const pnlChart = buildPnlChart(enhancedTxns, addr, solPrice);

        const ts = enhancedTxns.map(t => t.timestamp).filter(Boolean).sort((a,b) => a-b);
        const dateRange    = ts.length >= 2 ? `${fmtDate(ts[0])} - ${fmtDate(ts[ts.length-1])}`: ts.length === 1 ? fmtDate(ts[0]) : '—';
        const lastAnalysis = new Date().toLocaleString('fr-FR', { day:'2-digit', month:'2-digit', year:'numeric', hour:'2-digit', minute:'2-digit'});
        const now = Date.now() / 1000;
        const daysAct = Math.max(1, (now - (ts[0] || now)) / 86400);
        const swapCnt = enhancedTxns.filter(t => t.type === 'SWAP').length;
        const tpd = swapCnt / daysAct;
        const isBot = tpd > BOT_THRESHOLD_TPD;
        const verdict = buildFollowVerdict({ overallRoi: pnl.overallRoi, winRate: pnl.winRate, totalTrades: pnl.totalTrades, tradesPerDay: tpd, daysActive: Math.round(daysAct), rrRatio: pnl.rrRatio, avgGainUsd: pnl.gainMoyenUsd, avgLossUsd: pnl.perteMoyUsd, bestTradePnl: pnl.bestTrade?.pnlUsd ?? null, totalPnlUsd: pnl.pnlUsd, closedPositions: pnl.closedPositions, avgEntryDelayMin: pnl.avgEntryDelayMin });
        const ev = pnl.gainMoyenUsd > 0 && pnl.perteMoyUsd > 0 && pnl.winRate > 0
          ? (pnl.winRate / 100) * pnl.gainMoyenUsd - ((100 - pnl.winRate) / 100) * pnl.perteMoyUsd
          : null;

        let mainTag;
        if (isBot) mainTag = { label: 'Bot Probable', color: 'orange' };
        else if (verdict.recommendation === 'Suivre') mainTag = { label: 'Trader Fiable', color: 'green' };
        else if (verdict.recommendation === 'Ne pas suivre') mainTag = { label: 'À Éviter', color: 'red' };
        else mainTag = { label: 'Profil Neutre', color: 'gray' };

        const today = new Date();
        const calMap = calcPnlCalendar(pnl.closedPositions, today.getMonth(), today.getFullYear());
        const calFmt = {};
        for (const [d, v] of Object.entries(calMap)) calFmt[d] = fmtUsd(v, true);
        const calTot   = Object.values(calMap).reduce((s,v) => s+v, 0);
        const calProf  = Object.values(calMap).filter(v => v > 0).length;
        const calTotal = Object.keys(calMap).length;
        const calBest  = Math.max(0, ...Object.values(calMap));
        const confidence = buildConfidence({ txCount: enhancedTxns.length, swapCount: swapCnt, daysActive: Math.round(daysAct), hasApiKey: !!getApiKey(), closedTradesCount: pnl.totalTrades });

        const openPositionsCount = pnl.closedPositions.filter(p => p.resultat === 'En cours').length;

        const data = {
          address: addr, solBalance: solBal, solBalanceAvailable: false, solPrice,
          tokenCount: tokenAccs.length, dateRange, lastAnalysis,
          roi:          pnl.totalTrades > 0 ? fmtRoi(pnl.overallRoi) : '—',
          roiPos:       pnl.overallRoi >= 0,
          pnl:          pnl.totalTrades > 0 ? fmtUsd(pnl.pnlUsd, true) : '—',
          pnlPos:       pnl.pnlUsd >= 0,
          pnlSol:       `${pnl.pnlSol.toFixed(3)} SOL`,
          pnlSolRaw:    pnl.pnlSol,
          trades:       pnl.totalTrades,
          winRate:      pnl.totalTrades > 0 ? `${pnl.winRate.toFixed(1)}%`: '—',
          winRatePos:   pnl.winRate >= 50,
          gainMoyen:    pnl.gainMoyenUsd > 0 ? fmtUsd(pnl.gainMoyenUsd) : '—',
          perteMoyenne: pnl.perteMoyUsd  > 0 ? fmtUsd(pnl.perteMoyUsd)  : '—',
          tradesPerDay: `${tpd.toFixed(1)} trades/jour${isBot ? ' (bot)': ''}`,
          botScore:     isBot ? 94 : Math.min(30, Math.round(tpd * 2)),
          daysActive:   Math.round(daysAct),
          tags:         [mainTag],
          tendance:     pnl.overallRoi > 50 ? 'Anticipe les pumps': '—',
          topTokens:    pnl.tokenRows,
          tradesList:   pnl.tradesList,
          closedPositions: pnl.closedPositions,
          winsCount:    pnl.wins,
          lossesCount:  pnl.losses,
          totalAchatUsd: pnl.totalAchatUsd,
          totalVenteUsd: pnl.totalVenteUsd,
          rrRatio:      pnl.rrRatio !== null ? pnl.rrRatio.toFixed(2) : '—',
          maxStreak:    pnl.maxStreak || 0,
          bestTrade:    pnl.bestTrade,
          worstTrade:   pnl.worstTrade,
          dureeMoy:     fmtDuree(pnl.dureeMoyMin),
          dureeMoyMin:  pnl.dureeMoyMin,
          avgEntryDelayMin: pnl.avgEntryDelayMin,
          avgEntryDelay:    pnl.avgEntryDelayMin !== null ? fmtDuree(pnl.avgEntryDelayMin) : '—',
          heatmap, bestHeure: heatmap.bestHeure, bestJour: heatmap.bestJour,
          pnlChart,
          rawTxns:     enhancedTxns,
          rawAddr:     addr,
          rawSolPrice: solPrice,
          calHighlights:      calFmt,
          calMonthPnl:        fmtUsd(calTot, true),
          calJoursProfitables: calTotal > 0 ? `${calProf}/${calTotal}`: '0/0',
          calMeilleurJour:    calBest > 0 ? fmtUsd(calBest, true) : '—',
          hasApiKey: !!getApiKey(), hasSwaps: swapCnt > 0,
          analyzedTxCount: enhancedTxns.length, analyzedSwapCount: swapCnt,
          totalSwaps: swapCnt,
          closedTradesCount: pnl.totalTrades,
          openPositionsCount,
          confidenceScore: confidence.score, confidenceLevel: confidence.level, confidenceNote: confidence.note,
          followScore: verdict.score, followRecommendation: verdict.recommendation,
          followRisk: verdict.risk, followPositives: verdict.positives, followWarnings: verdict.warnings,
          expectedValue: ev,
        };

        try { localStorage.setItem(cacheKey, JSON.stringify({ data, timestamp: Date.now() })); } catch {}
        if (!cancelled) setState({ loading: false, error: null, data });
      } catch (e) {
        console.warn('Analyse erreur:', e.message);
        if (!cancelled) setState(prev => ({ ...prev, loading: false, error: e.message }));
      }
    })();

    return () => { cancelled = true; };
  }, [address, refreshKey]);

  return state;
}